import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["id"];
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { RightIcon } from 'foundations-assets/icons/components/RightIcon';
import { View } from 'foundations-components/components/View';
import { Button } from 'foundations-components/transitional/components/Button';
import { ButtonWrapper } from 'foundations-components/transitional/components/ButtonWrapper';
import CompanionFooter from './CompanionFooter';
import InteractiveContentError from './InteractiveContentError';
import InteractiveEducationLoading from './InteractiveEducationLoading';
import QuizMeQuestions from './QuizMeQuestions';
import UsageTracker from '../../../utils/UsageTracker';
import { QuizMeDifficultySelector } from './QuizMeDifficultySelector';
import { useAsyncGetQuizMeFromCache } from '../hooks/useGenerateAsyncQuizMe';
import { useLessonQuizMeFromCache } from '../hooks/useLessonQuizMeFromCache';
import { checkQuizMePollingStatus, clearPersistedDifficulty, persistDifficulty, useDifficultyPreference, useQuizMeHelpers, useQuizMeSession } from '../utils/quizMeUtils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const QuizMeTab = ({
  additionalTracking,
  asyncGenerateError,
  asyncGenerateLoading,
  contentType,
  lesson,
  lessonContentId,
  onAsyncRegenerateQuiz,
  onRegenerateSingleVideoQuiz,
  primaryContentId
}) => {
  var _getQuizState;
  const [questionAnswered, setQuestionAnswered] = useState(false);
  const [selectedAnswerIndex, setSelectedAnswerIndex] = useState();
  const [selectedAnswerCorrect, setSelectedAnswerCorrect] = useState();
  const [pendingDifficulty, setPendingDifficulty] = useState(null);
  const progressTracking = useMemo(() => {
    const _ref = additionalTracking || {},
      {
        id
      } = _ref,
      rest = _objectWithoutPropertiesLoose(_ref, _excluded);
    return Object.assign({
      contentId: id
    }, rest);
  }, [additionalTracking]);
  const {
    incrementQuestionWithAnswer,
    resetQuiz,
    getQuizState,
    getQuizMeRenderedTimestamp
  } = useQuizMeSession();
  const contentId = lessonContentId !== null && lessonContentId !== void 0 ? lessonContentId : primaryContentId;
  const quizMeStatus = (_getQuizState = getQuizState(contentId, contentType)) === null || _getQuizState === void 0 ? void 0 : _getQuizState.quizMeStatus;
  const isShorts = contentType === 'SHORT_FORM_CONTENT';
  const isLesson = contentType === 'LESSON';
  const isDifficultyEnabled = isShorts || isLesson;
  const persistedDifficulty = useDifficultyPreference(contentId);
  const selectedDifficulty = isDifficultyEnabled ? persistedDifficulty : undefined;
  const {
    formatAsyncQuizMeQuestions
  } = useQuizMeHelpers();

  // Tracks time from user clicking "Quiz Me" to questions appearing — measures perceived load time.
  // Ref ensures we only fire once per content; the effect's deps re-run on every quiz state update.
  const trackedRenderKeyRef = useRef(null);
  useEffect(() => {
    var _getQuizState2;
    if (quizMeStatus !== 'COMPLETED') {
      trackedRenderKeyRef.current = null;
      return;
    }
    if (!additionalTracking) return;
    const key = `${contentType}:${contentId}`;
    if (trackedRenderKeyRef.current === key) return;
    trackedRenderKeyRef.current = key;
    const clickTimestamp = (_getQuizState2 = getQuizState(contentId, contentType)) === null || _getQuizState2 === void 0 ? void 0 : _getQuizState2.clickTimestamp;
    const renderedTimestamp = getQuizMeRenderedTimestamp({
      contentType,
      contentId
    });
    const elapsedTime = clickTimestamp !== undefined ? renderedTimestamp - clickTimestamp : undefined;
    UsageTracker.trackStandalone('Learning Center Interaction', Object.assign({
      action: 'ai-quiz-me-rendered',
      id: primaryContentId,
      contentType,
      timestamp: elapsedTime,
      level: selectedDifficulty
    }, additionalTracking));
  }, [primaryContentId, contentType, additionalTracking, quizMeStatus, contentId, selectedDifficulty, getQuizState, getQuizMeRenderedTimestamp]);
  const lessonQuizMeData = useLessonQuizMeFromCache({
    contents: lesson === null || lesson === void 0 ? void 0 : lesson.content,
    lessonContentId,
    selectedDifficulty
  });
  const shortsQuizMeData = useAsyncGetQuizMeFromCache({
    primaryContentId,
    lessonContentId,
    contentType,
    selectedDifficulty,
    skip: isLesson
  });
  const quizMeData = isLesson ? lessonQuizMeData : shortsQuizMeData;
  const {
    quizReady
  } = checkQuizMePollingStatus(quizMeData ? {
    tasks: [quizMeData]
  } : undefined);
  const {
    currentCorrelationId,
    quizQuestions
  } = useMemo(() => formatAsyncQuizMeQuestions({
    lessonContentId,
    contentType,
    initialQuizTasks: quizMeData ? {
      tasks: [quizMeData]
    } : undefined,
    primaryContentId
  }), [formatAsyncQuizMeQuestions, lessonContentId, contentType, quizMeData, primaryContentId]);
  const isPerVideoError = quizMeStatus === 'FAILED';
  const isMutationError = !quizMeStatus && asyncGenerateError;
  const isError = isPerVideoError || isMutationError;
  const isLoading = !isError && (!quizReady && !quizMeStatus || quizMeStatus === 'PROCESSING' || quizMeStatus === 'PENDING' || quizMeStatus === 'COMPLETED' && !quizReady);
  const isShowingSelector = isDifficultyEnabled && !selectedDifficulty;
  const difficultyConfirmedRef = useRef(false);
  const additionalTrackingRef = useRef(additionalTracking);
  additionalTrackingRef.current = additionalTracking;

  // Tracks when the user sees the selector but closes the panel without confirming — signals drop-off.
  useEffect(() => {
    if (!isShowingSelector) return;
    difficultyConfirmedRef.current = false;
    return () => {
      if (!difficultyConfirmedRef.current) {
        UsageTracker.trackStandalone('Learning Center Interaction', Object.assign({
          action: 'ai-quiz-me-selector-abandoned',
          contentId: primaryContentId,
          contentType
        }, additionalTrackingRef.current));
      }
    };
  }, [isShowingSelector, primaryContentId, contentType]);
  const resetQuizState = useCallback(() => {
    resetQuiz({
      contentType,
      videoContentId: contentId
    });
    setQuestionAnswered(false);
    setSelectedAnswerIndex(undefined);
    setSelectedAnswerCorrect(undefined);
  }, [contentType, contentId, resetQuiz]);
  const handleConfirmDifficulty = useCallback(() => {
    const difficulty = pendingDifficulty !== null && pendingDifficulty !== void 0 ? pendingDifficulty : 'INTERMEDIATE';
    difficultyConfirmedRef.current = true;
    persistDifficulty(contentId, difficulty);
    // Tracks which difficulty the user confirmed — primary input for difficulty distribution metrics.
    // selectionMethod: 'explicit' = user picked a difficulty; 'default' = user skipped and started directly.
    UsageTracker.trackStandalone('Learning Center Interaction', Object.assign({
      action: 'ai-quiz-me-difficulty-selected',
      contentId: primaryContentId,
      contentType,
      level: difficulty,
      selectionMethod: pendingDifficulty ? 'explicit' : 'default'
    }, additionalTracking));
  }, [additionalTracking, contentId, contentType, pendingDifficulty, primaryContentId]);
  const handleChangeDifficulty = useCallback(() => {
    const previousDifficulty = selectedDifficulty;
    clearPersistedDifficulty(contentId);
    setPendingDifficulty(null);
    resetQuizState();
    // Tracks mid-session difficulty changes — high rate signals wrong default; near-zero signals poor discoverability.
    UsageTracker.trackStandalone('Learning Center Interaction', Object.assign({
      action: 'ai-quiz-me-difficulty-changed',
      contentId: primaryContentId,
      contentType,
      valueFrom: previousDifficulty
    }, additionalTracking));
  }, [additionalTracking, contentId, contentType, primaryContentId, resetQuizState, selectedDifficulty]);
  const handleSelectAnswerIndexChange = (answerIndex, correct) => {
    setSelectedAnswerIndex(answerIndex);
    setSelectedAnswerCorrect(correct);
  };
  const handleCheckAnswer = () => {
    UsageTracker.trackStandalone('Content Progress', Object.assign({}, progressTracking, {
      action: 'ai-quiz-me-answered-question',
      value: selectedAnswerCorrect ? 'ai-quiz-me-answer-correct' : 'ai-quiz-me-answer-incorrect',
      level: selectedDifficulty
    }));
    incrementQuestionWithAnswer({
      contentType,
      contentId: lessonContentId !== null && lessonContentId !== void 0 ? lessonContentId : primaryContentId,
      correct: !!selectedAnswerCorrect
    });
    setQuestionAnswered(true);
  };
  const handleNext = () => {
    setQuestionAnswered(false);
    setSelectedAnswerIndex(undefined);
    setSelectedAnswerCorrect(undefined);
  };
  const handleRetakeQuiz = () => {
    UsageTracker.trackStandalone('Content Interaction', {
      action: 'ai-quiz-me-retake',
      contentId: primaryContentId,
      contentType,
      visibility: additionalTracking === null || additionalTracking === void 0 ? void 0 : additionalTracking.visibility,
      level: selectedDifficulty
    });
    resetQuizState();
  };
  const handleRetry = () => onRegenerateSingleVideoQuiz && lessonContentId && !asyncGenerateLoading ? onRegenerateSingleVideoQuiz(lessonContentId, selectedDifficulty !== null && selectedDifficulty !== void 0 ? selectedDifficulty : 'INTERMEDIATE') : onAsyncRegenerateQuiz();
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(View, {
      "data-test-id": "ai-quiz-me-tab-container",
      overflow: "hidden",
      paddingBlockStart: "700",
      paddingInline: "700",
      children: isShowingSelector ? /*#__PURE__*/_jsx(QuizMeDifficultySelector, {
        onDifficultyChange: setPendingDifficulty,
        selectedDifficulty: pendingDifficulty
      }) : isLoading ? /*#__PURE__*/_jsx(InteractiveEducationLoading, {
        carouselTexts: [/*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.interactiveEducation.loading.quiz.fact1"
        }, "fact1"), /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.interactiveEducation.loading.quiz.fact2"
        }, "fact2"), /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.interactiveEducation.loading.quiz.fact3"
        }, "fact3"), /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.interactiveEducation.loading.quiz.fact4"
        }, "fact4")],
        mainText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.interactiveEducation.loading.buildingQuiz"
        })
      }) : isError || !quizReady ? /*#__PURE__*/_jsx(InteractiveContentError, {
        handleRetry: handleRetry,
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.interactiveEducation.quiz.quizMe"
        })
      }) : /*#__PURE__*/_jsx(QuizMeQuestions, {
        additionalTracking: progressTracking,
        contentType: contentType,
        difficultyLevel: selectedDifficulty,
        handleRestartQuiz: handleRetakeQuiz,
        handleSelectAnswerIndexChange: handleSelectAnswerIndexChange,
        lessonContentId: lessonContentId,
        onChangeDifficulty: isDifficultyEnabled ? handleChangeDifficulty : undefined,
        primaryContentId: primaryContentId,
        questionAnswered: questionAnswered,
        quizQuestions: quizQuestions,
        selectedAnswerIndex: selectedAnswerIndex
      })
    }), /*#__PURE__*/_jsx(CompanionFooter, {
      additionalButtons: /*#__PURE__*/_jsx(ButtonWrapper, {
        children: isShowingSelector ? /*#__PURE__*/_jsxs(Button, {
          onClick: handleConfirmDifficulty,
          use: "tertiary",
          children: [/*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.interactiveEducation.quiz.difficulty.startQuiz"
          }), /*#__PURE__*/_jsx(RightIcon, {})]
        }) : /*#__PURE__*/_jsxs(_Fragment, {
          children: [!questionAnswered && selectedAnswerIndex !== undefined && /*#__PURE__*/_jsxs(Button, {
            onClick: handleCheckAnswer,
            use: "tertiary",
            children: [/*#__PURE__*/_jsx(FormattedMessage, {
              message: "learningCenter.interactiveEducation.quiz.checkAnswer"
            }), /*#__PURE__*/_jsx(RightIcon, {})]
          }), questionAnswered && /*#__PURE__*/_jsxs(Button, {
            onClick: handleNext,
            use: "tertiary",
            children: [/*#__PURE__*/_jsx(FormattedMessage, {
              message: "learningCenter.interactiveEducation.quiz.next"
            }), /*#__PURE__*/_jsx(RightIcon, {})]
          })]
        })
      }),
      contentType: contentType,
      correlationId: currentCorrelationId,
      featureId: "QUIZ_ME",
      primaryContentId: primaryContentId,
      trackingAction: "AI_QUIZ_ME_FEEDBACK"
    })]
  });
};
export default QuizMeTab;